/**
 * Pure AI Fashion session DTOs and reducers.
 *
 * Extracted from `AIFashionHandler` so that any future surface — the editor
 * adapter today, a flagged AIStudio tomorrow, tests, etc. — can share
 * one canonical session shape and reducer set without dragging in fabric
 * or canvas-focused handler code. Everything in this file is editor-free
 * and unit-testable in isolation.
 *
 * Behavior parity:
 *   - `DEFAULT_SESSION` mirrors the previous AIFashionHandler default exactly.
 *   - `applyGenerationParams` mutates the session in place to match the
 *     previous handler implementation byte-for-byte (same field set,
 *     same source field for `metadataProductKey`).
 *   - The query helpers (`isGeneratedObject`, `getProductGuidForObject`)
 *     wrap straight history lookups so consumers never reach into the
 *     session structure directly.
 */

import type {
  AIModelsPromptMetadata,
  AIModelsPoseComposition,
} from '@/store/slices/aiFashionModels/types'
import type { VideoResolution, VideoDuration } from '@/store/slices/aiFashion/types'

// ─── Public DTOs ──────────────────────────────────────────────────────────────

export interface AIFashionTemplate {
  id: string
  title: string
  imageUrl: string
  prompt?: string
  gender?: string
  /** Age category from the template (`age_category`), e.g. 'baby', 'toddler', 'preteen'. */
  age?: string
  categoryId?: string
}

export interface PoseTile {
  src: string
  title: string
  composition: AIModelsPoseComposition
}

export type AspectRatioOption =
  | 'square'
  | 'portrait'
  | 'portrait_4_5'
  | 'portrait_2_3'
  | 'portrait_9_16'

/** Reference to an AI Fashion image. AI Studio keeps `dataUrl` as the stable source;
 * repo ids are compatibility metadata for editor-backed paths. */
export interface RepoEntry {
  guid: string
  assetStateId: string
  dataUrl?: string | null
}

/**
 * Snapshot of all generation parameters used for a single AI Fashion run.
 * Persisted alongside the result on the canvas object so a later "create
 * variation" or "restore params" can rebuild the exact session state.
 */
export interface GenerationSnapshot {
  template: AIFashionTemplate
  promptMetadata: AIModelsPromptMetadata | null
  backgroundPromptOverride: string | null
  poseOverride: string | null
  compositionOverride: AIModelsPoseComposition | null
  /** Key into the handler's `poseTileCache` for the tile image used during generation. */
  poseSelectedTileKey: string | null
  aspectRatioOption: AspectRatioOption | null
  metadataGenderKey: string | null
  /** The product (input image) that was used for this specific generation. */
  product: RepoEntry
  /** For editWithAI results: the source generated image used as the editing reference. */
  referenceImage?: RepoEntry
}

export interface GeneratedObjectRecord {
  /** Distinguishes a real generation result from a product-layer snapshot used for param-restore/variation. */
  kind: 'generated' | 'product'
  /** The generated output image stored in the repository. */
  result: RepoEntry
  generationParams: GenerationSnapshot
}

export interface AIFashionSession {
  selectedTemplate: AIFashionTemplate | null
  promptMetadata: AIModelsPromptMetadata | null
  backgroundPromptOverride: string | null
  poseOverride: string | null
  compositionOverride: AIModelsPoseComposition | null
  /** Key into the handler's `poseTileCache` — not the image data itself. */
  poseSelectedTileKey: string | null
  aspectRatioOption: AspectRatioOption | null
  productImageCacheKey: string | null
  cachedProductBase64: string | null
  /** Repo entry for the current product, used as the "before" reference in inputImage mode. */
  productRepoEntry: RepoEntry | null
  cachedLastGeneratedImage: string | null
  metadataGenderKey: string | null
  metadataProductKey: string | null
  generatedObjectHistory: Record<string, GeneratedObjectRecord>
  /** The id of the most recently generated object (insertion-order-independent lookup for generatedBefore flow). */
  lastGeneratedObjectId: string | null
  /** Last-used video resolution — persisted so re-generate uses the same setting. */
  videoResolution: VideoResolution
  /** Last-used video duration — persisted so re-generate uses the same setting. */
  videoDuration: VideoDuration
}

// ─── Defaults ─────────────────────────────────────────────────────────────────

export const DEFAULT_SESSION: AIFashionSession = {
  selectedTemplate: null,
  promptMetadata: null,
  backgroundPromptOverride: null,
  poseOverride: null,
  compositionOverride: null,
  poseSelectedTileKey: null,
  aspectRatioOption: 'portrait',
  productImageCacheKey: null,
  cachedProductBase64: null,
  productRepoEntry: null,
  cachedLastGeneratedImage: null,
  metadataGenderKey: null,
  metadataProductKey: null,
  generatedObjectHistory: {},
  lastGeneratedObjectId: null,
  videoResolution: '480p',
  videoDuration: 4,
}

/** Returns a fresh, empty session. Use instead of `{ ...DEFAULT_SESSION }` so
 *  the `generatedObjectHistory` map is also a fresh object (the spread on
 *  `DEFAULT_SESSION` would otherwise share the same `{}` reference forever). */
export function createDefaultSession(): AIFashionSession {
  return { ...DEFAULT_SESSION, generatedObjectHistory: {} }
}

// ─── Reducers ─────────────────────────────────────────────────────────────────

/**
 * Apply a generation snapshot's parameters to the current session in place.
 * Only generation parameters are updated — product state
 * (`cachedProductBase64`, `productImageCacheKey`, `productRepoEntry`) is
 * left intact.
 */
export function applyGenerationParams(
  session: AIFashionSession,
  snapshot: GenerationSnapshot,
): void {
  session.selectedTemplate = snapshot.template
  session.promptMetadata = snapshot.promptMetadata
  session.backgroundPromptOverride = snapshot.backgroundPromptOverride
  session.poseOverride = snapshot.poseOverride
  session.compositionOverride = snapshot.compositionOverride
  session.poseSelectedTileKey = snapshot.poseSelectedTileKey
  session.aspectRatioOption = snapshot.aspectRatioOption
  session.metadataGenderKey = snapshot.metadataGenderKey
  session.metadataProductKey = snapshot.product.guid
}

/**
 * Apply the generation params for a previously generated object back into
 * the session. Returns `true` when the object is in history, `false`
 * otherwise. Does not touch product state.
 */
export function loadParamsFromGeneratedObject(
  session: AIFashionSession,
  objectId: string | null | undefined,
): boolean {
  if (!objectId) return false
  const record = session.generatedObjectHistory[objectId]
  if (!record?.generationParams) return false
  applyGenerationParams(session, record.generationParams)
  return true
}

// ─── Queries ──────────────────────────────────────────────────────────────────

export function isGeneratedObject(
  session: AIFashionSession,
  objectId: string | null | undefined,
): boolean {
  if (!objectId) return false
  const record = session.generatedObjectHistory[objectId]
  return record?.kind === 'generated'
}

/**
 * Returns the product GUID used to generate the given object, or null if
 * the object is not in history. Used to key the pose tile cache per product.
 */
export function getProductGuidForObject(
  session: AIFashionSession,
  objectId: string | null | undefined,
): string | null {
  if (!objectId) return null
  return session.generatedObjectHistory[objectId]?.generationParams.product.guid ?? null
}
